import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import * as Icons from 'lucide-react';
import { PortfolioItem } from '../utils/data';
import { getPortfolioImages, normalizeWebsiteUrl, resolveUploadUrl, useSiteConfig } from '../utils/siteConfig';

interface PortfolioProjectModalProps {
  project: PortfolioItem;
  allProjects?: PortfolioItem[];
  onSelectProject?: (project: PortfolioItem) => void;
  onClose: () => void;
}

// Helper to check if a URL is a video file or video platform
function isVideoUrl(url: string | undefined): boolean {
  if (!url) return false;
  const clean = url.toLowerCase().split('?')[0];
  return (
    clean.endsWith('.mp4') ||
    clean.endsWith('.webm') ||
    clean.endsWith('.ogg') ||
    clean.endsWith('.mov') ||
    url.includes('youtube.com') ||
    url.includes('youtu.be') ||
    url.includes('vimeo.com')
  );
}

// Helper to extract YouTube ID
function getYouTubeEmbedUrl(url: string): string | null {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? `https://www.youtube.com/embed/${match[2]}?autoplay=1&mute=0` : null;
}

export function PortfolioProjectModal({
  project,
  allProjects = [],
  onSelectProject,
  onClose
}: PortfolioProjectModalProps) {
  const { openGetStarted } = useSiteConfig();
  const rawImages = getPortfolioImages(project);
  const images = rawImages.length > 0 ? rawImages : (project.image ? [project.image] : []);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const websiteUrl = normalizeWebsiteUrl(project.websiteUrl);

  const currentProjectIndex = allProjects.findIndex(p => p.id === project.id || p.title === project.title);
  const hasMultipleProjects = allProjects.length > 1;

  useEffect(() => {
    setActiveMediaIndex(0);
    setIsZoomed(false);
  }, [project]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isZoomed) {
          setIsZoomed(false);
        } else {
          onClose();
        }
      } else if (event.key === 'ArrowLeft') {
        if (event.shiftKey && hasMultipleProjects && onSelectProject) {
          // Prev project
          const prevIdx = (currentProjectIndex - 1 + allProjects.length) % allProjects.length;
          onSelectProject(allProjects[prevIdx]);
        } else if (images.length > 1) {
          // Prev media
          setActiveMediaIndex(current => (current - 1 + images.length) % images.length);
        }
      } else if (event.key === 'ArrowRight') {
        if (event.shiftKey && hasMultipleProjects && onSelectProject) {
          // Next project
          const nextIdx = (currentProjectIndex + 1) % allProjects.length;
          onSelectProject(allProjects[nextIdx]);
        } else if (images.length > 1) {
          // Next media
          setActiveMediaIndex(current => (current + 1) % images.length);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [images.length, onClose, isZoomed, hasMultipleProjects, currentProjectIndex, allProjects, onSelectProject]);

  const showPreviousMedia = () => {
    setActiveMediaIndex(current => (current - 1 + images.length) % images.length);
  };

  const showNextMedia = () => {
    setActiveMediaIndex(current => (current + 1) % images.length);
  };

  const goToPrevProject = () => {
    if (!hasMultipleProjects || !onSelectProject) return;
    const prevIdx = (currentProjectIndex - 1 + allProjects.length) % allProjects.length;
    onSelectProject(allProjects[prevIdx]);
  };

  const goToNextProject = () => {
    if (!hasMultipleProjects || !onSelectProject) return;
    const nextIdx = (currentProjectIndex + 1) % allProjects.length;
    onSelectProject(allProjects[nextIdx]);
  };

  const activeMediaUrl = images[activeMediaIndex] || '';
  const isCurrentMediaVideo = isVideoUrl(activeMediaUrl);
  const youtubeEmbedUrl = isCurrentMediaVideo ? getYouTubeEmbedUrl(activeMediaUrl) : null;

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-slate-950/95 backdrop-blur-2xl text-white overflow-hidden"
      role="dialog"
      aria-modal="true"
      aria-label={`${project.title} full screen showcase`}
    >
      {/* ── TOP IMMERSIVE HEADER BAR ── */}
      <header className="shrink-0 z-30 flex items-center justify-between px-4 sm:px-8 py-3.5 border-b border-white/10 bg-slate-950/80 backdrop-blur-md">
        {/* Left: Project identity & quick category */}
        <div className="flex items-center gap-3 min-w-0 pr-4">
          <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {project.category}
          </span>
          <h2 className="text-base sm:text-xl font-black text-white truncate tracking-tight">
            {project.title}
          </h2>
        </div>

        {/* Right: Quick project switcher & actions */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Project Carousel controls if multiple projects */}
          {hasMultipleProjects && onSelectProject && (
            <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl p-1 text-xs">
              <button
                onClick={goToPrevProject}
                type="button"
                className="p-1.5 hover:bg-white/10 rounded-lg text-slate-300 hover:text-white transition"
                title="Previous Project (Shift + ←)"
              >
                <Icons.ChevronLeft size={16} />
              </button>
              <span className="px-2 font-mono font-medium text-slate-400 text-[11px]">
                {currentProjectIndex + 1} / {allProjects.length}
              </span>
              <button
                onClick={goToNextProject}
                type="button"
                className="p-1.5 hover:bg-white/10 rounded-lg text-slate-300 hover:text-white transition"
                title="Next Project (Shift + →)"
              >
                <Icons.ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* Visit Website shortcut */}
          {websiteUrl && (
            <a
              href={websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary to-accent hover:brightness-110 text-white rounded-xl text-xs font-bold transition shadow-lg shadow-primary/20"
            >
              <span>Live Website</span>
              <Icons.ExternalLink size={13} />
            </a>
          )}

          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-red-500/20 text-slate-300 hover:text-red-400 border border-white/10 hover:border-red-500/30 transition shadow-sm"
            aria-label="Close showcase (Esc)"
            title="Close (Esc)"
          >
            <Icons.X size={18} />
          </button>
        </div>
      </header>

      {/* ── MAIN SHOWCASE STAGE ── */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden relative">
        {/* ── LEFT: MEDIA THEATER (GALLERY & FULLSCREEN VIEWER) ── */}
        <div className="relative flex-1 flex flex-col justify-between bg-black/60 overflow-hidden min-h-[340px] lg:min-h-0">
          {/* Main active media viewport */}
          <div
            ref={mediaContainerRef}
            className="relative flex-1 flex items-center justify-center p-3 sm:p-6 select-none overflow-hidden"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={`${project.id || project.title}-${activeMediaIndex}`}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: isZoomed ? 1.25 : 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.3 }}
                className={`relative flex items-center justify-center max-h-full max-w-full transition-transform duration-300 ${
                  isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'
                }`}
                onClick={() => !isCurrentMediaVideo && setIsZoomed(!isZoomed)}
              >
                {isCurrentMediaVideo ? (
                  youtubeEmbedUrl ? (
                    <iframe
                      src={youtubeEmbedUrl}
                      title={`${project.title} video`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-[85vw] max-w-4xl aspect-video rounded-2xl shadow-2xl border border-white/15 bg-black"
                    />
                  ) : (
                    <video
                      src={resolveUploadUrl(activeMediaUrl)}
                      controls
                      autoPlay
                      playsInline
                      className="max-h-[68vh] max-w-full rounded-2xl shadow-2xl border border-white/10 bg-black object-contain"
                    />
                  )
                ) : activeMediaUrl ? (
                  <img
                    src={resolveUploadUrl(activeMediaUrl)}
                    alt={`${project.title} showcase ${activeMediaIndex + 1}`}
                    className="max-h-[68vh] max-w-full rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] border border-white/10 object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center gap-3 text-slate-500 p-12">
                    <Icons.ImageOff size={48} />
                    <span className="text-sm font-medium">No media uploaded</span>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Left & Right navigation arrows */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={showPreviousMedia}
                  className="absolute left-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-slate-950/70 hover:bg-slate-900 text-white border border-white/20 shadow-2xl backdrop-blur-md transition hover:scale-110 active:scale-95 focus:outline-none"
                  aria-label="Previous image (←)"
                >
                  <Icons.ChevronLeft size={24} />
                </button>
                <button
                  type="button"
                  onClick={showNextMedia}
                  className="absolute right-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-slate-950/70 hover:bg-slate-900 text-white border border-white/20 shadow-2xl backdrop-blur-md transition hover:scale-110 active:scale-95 focus:outline-none"
                  aria-label="Next image (→)"
                >
                  <Icons.ChevronRight size={24} />
                </button>
              </>
            )}

            {/* Media overlay badges */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              {isCurrentMediaVideo ? (
                <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/80 text-white text-xs font-bold backdrop-blur-md shadow-lg">
                  <Icons.Play size={12} className="fill-current" /> Video Player
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsZoomed(!isZoomed)}
                  className="flex items-center gap-1 px-3 py-1 rounded-full bg-slate-900/80 hover:bg-slate-800 text-slate-200 text-xs font-medium border border-white/10 backdrop-blur-md transition"
                >
                  {isZoomed ? <Icons.Minimize2 size={12} /> : <Icons.Maximize2 size={12} />}
                  <span>{isZoomed ? 'Reset View' : 'Zoom'}</span>
                </button>
              )}
            </div>

            {images.length > 1 && (
              <span className="absolute bottom-4 right-4 rounded-full bg-slate-950/80 border border-white/10 px-3.5 py-1 text-xs font-mono font-bold text-slate-300 backdrop-blur-md shadow-lg">
                {activeMediaIndex + 1} / {images.length}
              </span>
            )}
          </div>

          {/* Bottom Thumbnail Strip */}
          {images.length > 1 && (
            <div className="shrink-0 flex items-center gap-3 overflow-x-auto p-3 sm:p-4 bg-slate-950/90 border-t border-white/10 no-scrollbar justify-center">
              {images.map((imgUrl, idx) => {
                const isVideo = isVideoUrl(imgUrl);
                const isActive = activeMediaIndex === idx;
                return (
                  <button
                    key={`${imgUrl}-${idx}`}
                    type="button"
                    onClick={() => setActiveMediaIndex(idx)}
                    className={`relative h-14 w-20 sm:h-16 sm:w-24 shrink-0 rounded-xl overflow-hidden border-2 transition-all duration-200 ${
                      isActive
                        ? 'border-primary ring-2 ring-primary/40 scale-105 opacity-100 shadow-lg'
                        : 'border-white/10 opacity-50 hover:opacity-90'
                    }`}
                  >
                    {isVideo ? (
                      <div className="w-full h-full bg-slate-900 flex items-center justify-center text-slate-300">
                        <Icons.Play size={20} className="fill-current" />
                      </div>
                    ) : (
                      <img
                        src={resolveUploadUrl(imgUrl)}
                        alt={`Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                    {isVideo && (
                      <span className="absolute bottom-1 right-1 px-1 rounded bg-black/80 text-[9px] font-bold text-white uppercase">
                        Vid
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── RIGHT: PROJECT DOSSIER & DETAILED STORY ── */}
        <div className="w-full lg:w-[460px] xl:w-[520px] shrink-0 flex flex-col justify-between overflow-y-auto bg-slate-900/90 border-t lg:border-t-0 lg:border-l border-white/10 p-6 sm:p-8 lg:p-10 backdrop-blur-xl">
          <div className="space-y-6">
            {/* Header info */}
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-2">
                {project.category}
              </span>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight leading-tight">
                {project.title}
              </h1>
            </div>

            {/* Technologies & Tags */}
            {project.tags && (
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2.5">
                  Tech Stack & Deliverables
                </h4>
                <div className="flex flex-wrap gap-2">
                  {project.tags.split(',').map((tag, tagIdx) => (
                    <span
                      key={tagIdx}
                      className="px-3 py-1 rounded-lg bg-white/5 border border-white/10 text-slate-300 text-xs font-medium tracking-wide shadow-sm"
                    >
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <hr className="border-white/10" />

            {/* Detailed Description & Overview */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
                <Icons.FileText size={14} className="text-primary" />
                Project Case Study & Overview
              </h4>
              <div className="text-slate-300 text-sm sm:text-base leading-relaxed font-sans space-y-4 whitespace-pre-line">
                <p>{project.description}</p>
              </div>
            </div>

            {/* Highlights Grid */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div className="p-3.5 rounded-2xl bg-white/5 border border-white/10">
                <span className="text-[11px] uppercase font-bold text-slate-400 block mb-1">Status</span>
                <span className="text-xs sm:text-sm font-bold text-emerald-400 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" /> Completed & Live
                </span>
              </div>
              <div className="p-3.5 rounded-2xl bg-white/5 border border-white/10">
                <span className="text-[11px] uppercase font-bold text-slate-400 block mb-1">Agency Role</span>
                <span className="text-xs sm:text-sm font-bold text-white">Full-Service Digital</span>
              </div>
            </div>
          </div>

          {/* Action Buttons & Links */}
          <div className="pt-8 mt-6 border-t border-white/10 space-y-3">
            {websiteUrl && (
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3.5 px-6 rounded-2xl bg-gradient-to-r from-primary to-accent hover:brightness-110 text-white font-bold text-sm sm:text-base transition shadow-xl shadow-primary/25 hover:-translate-y-0.5 active:translate-y-0"
              >
                <span>Visit Live Project Website</span>
                <Icons.ExternalLink size={18} />
              </a>
            )}

            <button
              type="button"
              onClick={() => {
                onClose();
                openGetStarted();
              }}
              className="flex items-center justify-center gap-2 w-full py-3.5 px-6 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 hover:text-white font-bold text-sm sm:text-base transition shadow-md hover:-translate-y-0.5 active:translate-y-0"
            >
              <Icons.Sparkles size={16} className="text-accent" />
              <span>Start Similar Project</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

